import { MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePermissions } from '@/features/auth';
import { useMarketTimeZone, useSelectedCountry } from '@/features/country-scope';
import { formatDateTime, formatRelativeDate } from '@/shared/lib/format';

import { useUnreadComplaintMessages } from '../api/complaints.queries';
import type { UnreadComplaintMessage } from '../types/complaint.types';

/**
 * Messages: complaint conversations where the customer wrote last and you have
 * not read it — yours or unassigned. One entry per conversation however many
 * messages are waiting, and a toast when a new one arrives. Needs
 * complaints.manage; renders nothing without it.
 */
export function ComplaintMessagesMenu() {
  const { can } = usePermissions();
  if (!can('complaints.manage')) return null;
  return <ComplaintMessagesMenuInner />;
}

function ComplaintMessagesMenuInner() {
  const { country } = useSelectedCountry();
  const zone = useMarketTimeZone();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const messages = useUnreadComplaintMessages(country?.id, true);
  const items = messages.data ?? NO_MESSAGES;
  useNewMessageToast(messages.data, navigate);

  const count = items.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8"
          aria-label={
            count > 0
              ? `Messages: ${count} ${count === 1 ? 'conversation' : 'conversations'} waiting for you`
              : 'Messages: nothing waiting'
          }
        >
          <MessageCircle className="size-4" aria-hidden />
          {count > 0 && (
            <span className="bg-destructive absolute -end-0.5 -top-0.5 min-w-4 rounded-full px-1 text-[10px] leading-4 font-semibold text-white tabular-nums">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <p className="text-xs font-semibold">Messages</p>
          <Link
            to="/complaints"
            onClick={() => setOpen(false)}
            className="text-primary text-[11px] hover:underline"
          >
            All complaints
          </Link>
        </div>
        {messages.isLoading ? (
          <p className="text-muted-foreground px-3 py-4 text-xs">Loading…</p>
        ) : messages.error ? (
          <p className="text-muted-foreground px-3 py-4 text-xs">Messages could not be loaded.</p>
        ) : count === 0 ? (
          <p className="text-muted-foreground px-3 py-6 text-center text-xs">
            You are all caught up.
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto py-1" aria-label="Conversations waiting for you">
            {items.map((item) => (
              <li key={item.complaintId}>
                <Link
                  to={`/complaints/${item.complaintId}`}
                  onClick={() => setOpen(false)}
                  className="hover:bg-muted/60 flex items-start gap-2.5 px-3 py-2"
                >
                  <Avatar className="mt-0.5 size-8 shrink-0">
                    <AvatarFallback className="bg-status-brand-bg text-primary text-[11px] font-bold">
                      {initials(item.customerName ?? 'Customer')}
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs font-semibold">
                        {item.customerName ?? 'Customer'}
                      </span>
                      <time
                        dateTime={item.messageAt}
                        title={formatDateTime(item.messageAt, zone)}
                        className="text-primary shrink-0 text-[10px] font-medium"
                      >
                        {formatRelativeDate(item.messageAt)}
                      </time>
                    </span>
                    <span className="text-muted-foreground text-[11px]">
                      #{item.complaintId}
                      {item.complaintType ? ` · ${item.complaintType}` : ''}
                    </span>
                    {item.preview && (
                      <span className="text-foreground line-clamp-2 text-xs">{item.preview}</span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

const NO_MESSAGES: UnreadComplaintMessage[] = [];

/**
 * A toast when a conversation gets a new customer message after the first
 * load — not for what was already waiting when the page opened.
 */
function useNewMessageToast(
  items: UnreadComplaintMessage[] | undefined,
  navigate: ReturnType<typeof useNavigate>,
) {
  const seen = useRef<Map<number, string> | null>(null);

  useEffect(() => {
    if (!items) return; // not loaded yet: nothing to compare with
    const previous = seen.current;
    seen.current = new Map(items.map((item) => [item.complaintId, item.messageAt]));
    if (!previous) return;
    const fresh = items.find(
      (item) =>
        !previous.has(item.complaintId) || item.messageAt > (previous.get(item.complaintId) ?? ''),
    );
    if (!fresh) return;
    toast(`${fresh.customerName ?? 'A customer'} sent a message`, {
      description: fresh.preview ?? `Complaint #${fresh.complaintId}`,
      action: { label: 'Open', onClick: () => void navigate(`/complaints/${fresh.complaintId}`) },
    });
  }, [items, navigate]);
}

function initials(name: string) {
  return (
    name
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '?'
  );
}
